import { needsImgixServerSign, withByosImgixServerSign } from '@air/component-imgix-image';

import { type WorkspaceAgentPanelImageFit } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

export type GetWorkspaceAgentPanelImageSrcOptions = {
  /** Target width in CSS pixels. Imgix multiplies by `dpr` for the actual fetched size. */
  width?: number;
  /** Target height in CSS pixels. Imgix multiplies by `dpr` for the actual fetched size. */
  height?: number;
  /** imgix `fit` mode. Defaults to `'clip'`. */
  fit?: WorkspaceAgentPanelImageFit;
  /** Override the device pixel ratio. Defaults to `window.devicePixelRatio` (or 1 outside the browser). */
  dpr?: number;
};

/**
 * Build a display URL for panel images, adding imgix size params for `.imgix.net` hosts and routing
 * BYOS proxy URLs through the sign BFF. Non-imgix sources are returned unchanged so callers do not
 * branch on hostname.
 */
export const getWorkspaceAgentPanelImageSrc = (
  src: string,
  options: GetWorkspaceAgentPanelImageSrcOptions = {},
): string => {
  if (options.width === undefined && options.height === undefined) {
    return withByosImgixServerSign(src);
  }

  const dpr = options.dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const params = {
    auto: 'format',
    dpr,
    fit: options.fit ?? 'clip',
    ...(options.width !== undefined ? { w: options.width } : {}),
    ...(options.height !== undefined ? { h: options.height } : {}),
  };

  if (needsImgixServerSign(src)) {
    return withByosImgixServerSign(src, params);
  }

  try {
    const parsed = new URL(src);

    if (!parsed.hostname.endsWith('.imgix.net')) {
      return src;
    }

    if (options.width !== undefined) {
      parsed.searchParams.set('w', String(options.width));
    }
    if (options.height !== undefined) {
      parsed.searchParams.set('h', String(options.height));
    }
    parsed.searchParams.set('dpr', String(dpr));
    parsed.searchParams.set('fit', options.fit ?? 'clip');
    parsed.searchParams.set('auto', 'format');

    return parsed.toString();
  } catch {
    return src;
  }
};
